import React, { Fragment } from 'react'

import Image from 'next/image'
import Link from 'next/link'

import _ from 'lodash'
import parse from "html-react-parser"

import { PageHeader } from '../fuselage/theme/2027/blocks/page-header/page-header'
import { Text } from '../fuselage/blocks/text/text'
import { Cards, Card } from '../fuselage/blocks/cards/cards'
import { FlipFlop } from '../fuselage/blocks/flipflop/flipflop'
import { Floaters, Floater } from '../fuselage/blocks/floater/floater'
import { Billboard } from '../fuselage/blocks/billboard/billboard'
import { BillboardSlider } from "../fuselage/blocks/billboard-slider/billboard-slider"
import { SpeakerCarousel } from '../fuselage/blocks/speaker-carousel/speaker-carousel'
import { SpeakerCard } from '../fuselage/components/speaker-card/speaker-card'
import { SwiperSlide } from 'swiper/react'
import { ImpactFigures, ImpactFigure } from '../fuselage/blocks/impact-figures/impact-figures'
import { Video } from '../fuselage/blocks/video/video'
import { Form } from '../fuselage/blocks/form/form'
import { SpeakerGrid } from '../fuselage/layout/speaker-grid/speaker-grid'
import { OffsetCallout } from '../fuselage/blocks/offset-callout/offset-callout'
import { SectionDivider } from '../fuselage/theme/2026/blocks/section-divider/section-divider'
import { LogoGrid } from '../fuselage/layout/logo-grid/logo-grid'
import { Quote } from "../fuselage/components/quote/quote"


import partnersCSS from '../css/app/partners.module.css'


export const handleContentBlocks = ( blocks ) => {

    if ( !blocks ) return

    // console.log("content-blocks detected: ", blocks)

    const blocksHandle = process.env.NEXT_PUBLIC_SITE_HANDLE

    return (
        <Fragment>
            {blocks.map( block => {

                // page header
                if ( block.__typename === `${blocksHandle}ContentBlocks_pageHeader_BlockType` ) {

                    let theme
                    block.theme === 'default' ? theme = `bg-primary` : theme = `bg-${block.theme}`

                    return (
                        <PageHeader key={block.id} theme={theme}>
                            <div className="formatted">
                                <div dangerouslySetInnerHTML={{__html: block.body }} />
                            </div>
                        </PageHeader>
                    )
                }

                // text blocks
                if ( block.__typename === `${blocksHandle}ContentBlocks_text_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Text
                                theme={block.theme} 
                                gap={'md'} 
                                columns={block.columns} 
                                maxWidth={block.maxWidth}
                                center={block.center}
                            >
                                {block.textBlocks.map( (textBlock, index) => {
                                    return <div key={index} dangerouslySetInnerHTML={{__html: textBlock.body }} />
                                })}
                            </Text>
                        </Fragment>
                    )
                }

                // cards
                if ( block.__typename === `${blocksHandle}ContentBlocks_cards_BlockType` ) {

                    if ( !block.cards ) return 

                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Cards
                                theme={block.theme} 
                                gap={'md'} 
                                columns={block.columns} 
                                maxWidth={block.maxWidth}
                                center={block.center}
                            >

                                {
                                    block.cards.map( card => {
                                        return (
                                            <Card 
                                                key={card.id}
                                                hero={card.hero} 
                                                heroRatio={block.aspectRatio} 
                                                heroLayout={block.layout} 
                                                heroWidth={block.width}
                                                body={card.body} 
                                            />
                                        )
                                    })    
                                }

                            </Cards>
                        </Fragment>
                    )
                }

                // images
                if ( block.__typename === `${blocksHandle}ContentBlocks_images_BlockType` ) {
                    if ( !block.images.length ) return
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <section className={`block bg-${block.theme}`}>
                                <div className={`container columns-${block.columns} gap-${block.gap} maxw-${block.maxWidth}`} style={{ position: `relative`}}>
                                    {
                                        block.images.map( image => <Image key={image.url} src={image.url} width={image.width} height={image.height} layout={`responsive`} />)
                                    }
                                </div>
                            </section>
                        </Fragment>
                    )
                }

                // flip-flops
                if ( block.__typename === `${blocksHandle}ContentBlocks_flipFlops_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            {block.flipFlops.map( flipFlop => {
                                return (
                                    <FlipFlop key={flipFlop.id} theme={block.theme} hero={flipFlop.hero[0].url} flipped={block.flipStartOrder}>
                                        <div dangerouslySetInnerHTML={{__html: flipFlop.body }} />
                                    </FlipFlop>
                                )
                            })}
                        </Fragment>
                    )
                }

                // floaters
                if ( block.__typename === `${blocksHandle}ContentBlocks_floaters_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Floaters theme={block.theme} maxWidth={block.maxWidth} flipped={block.flipStartOrder} anchorId={block.anchorId}>
                                {block.floaters.map( floater => {
                                    return (
                                        <Floater key={floater.id} hero={floater.hero[0]} heroSize={floater.heroSize}>
                                            <div dangerouslySetInnerHTML={{__html: floater.body }} />
                                        </Floater>
                                    )
                                })}
                            </Floaters>
                        </Fragment>
                    )
                }

                // billboard
                if ( block.__typename === `${blocksHandle}ContentBlocks_billboard_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Billboard 
                                hero={block.hero[0]}
                                height={block.height}
                                tint={block.tint}
                                horizontalAlign={block.horizontalAlign}
                                verticalAlign={block.verticalAlign}
                                maxContentWidth={block.maxContentWidth}
                                anchorId={block.anchorId}
                            >
                                <div dangerouslySetInnerHTML={{__html: block.body }} />
                            </Billboard>
                        </Fragment>
                    )
                }

                // billboard slider
                if ( block.__typename === `${blocksHandle}ContentBlocks_billboardSlider_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <BillboardSlider anchorId={block.anchorId}>
                                {block.slides.map( slide => {
                                    return (
                                        <SwiperSlide key={slide.id} >
                                            <Billboard 
                                                hero={slide.hero[0]}
                                                height={block.height}
                                                tint={block.tint}
                                                horizontalAlign={block.horizontalAlign}
                                                verticalAlign={block.verticalAlign}
                                                maxContentWidth={block.maxContentWidth}
                                            >
                                                <div dangerouslySetInnerHTML={{__html: slide.body }} />
                                            </Billboard>
                                        </SwiperSlide>
                                    )
                                })}
                            </BillboardSlider>
                        </Fragment>
                    )
                }

                // impact figures
                if ( block.__typename === `${blocksHandle}ContentBlocks_impactFigures_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <ImpactFigures theme={block.theme} anchorId={block.anchorId}>
                                {block.impactFigures.map( impact => {
                                    return (
                                        <ImpactFigure key={impact.id} value={impact.figure} label={impact.label} theme={block.theme} />
                                    )
                                })}
                            </ImpactFigures>
                        </Fragment>
                    )
                }
                
                // video
                if ( block.__typename === `${blocksHandle}ContentBlocks_video_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Video theme={block.theme} maxWidth={block.maxWidth} videoUrl={block.videoShareUrl} />
                        </Fragment>
                    )
                }
                
                // form
                if ( block.__typename === `${blocksHandle}ContentBlocks_hubspotForm_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <Form 
                                theme={block.theme} 
                                maxWidth={block.maxWidth}
                                portalId={block.hubspotForm[0].portalId}
                                formId={block.hubspotForm[0].formId}
                                anchorId={block.anchorId}
                            >
                                {block.body}
                            </Form>
                        </Fragment>
                    )
                }

                // profile carousel
                if ( block.__typename === `${blocksHandle}ContentBlocks_profilesCarousel_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <SpeakerCarousel 
                                heading={block.heading} 
                                text={block.text}
                                decal={'bg-default'}
                                ctaLink={block.ctaButton[0].buttonLink}
                                ctaLabel={block.ctaButton[0].buttonLabel}
                                anchorId={block.anchorId}
                            >
                                {
                                    block.profiles.map( profile => (
                                        <SpeakerCard
                                            key={profile.id}
                                            url={`/speakers/profile/${profile.slug}`}
                                            photoUrl={profile.avatar[0] ? profile.avatar[0].url : null }
                                            fullName={profile.title}
                                            jobTitle={profile.positions[0].jobTitle}
                                            company={profile.positions[0].organisation}
                                        />
                                    ))
                                }
                            </SpeakerCarousel>
                        </Fragment>
                    )
                }

                // offset callout
                if ( block.__typename === `${blocksHandle}ContentBlocks_offsetCallout_BlockType` ) {
                    
                    let theme
                    block.theme === 'default' ? theme = `bg-white` : theme = `bg-${block.theme}`

                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <OffsetCallout 
                                hero={block.hero && block.hero[0]} 
                                followsHeader={ block.followsPageHeader ? true : false } 
                                theme={theme} 
                                contentGraphic={ block.graphicOverlayText && block.graphicOverlayText[0] }
                                imageGraphic={ block.graphicOverlayImage && block.graphicOverlayImage[0] }
                            >
                                <div className="formatted">
                                    <div dangerouslySetInnerHTML={{__html: block.body }} />
                                </div>
                            </OffsetCallout>

                        </Fragment>
                    )
                }

                // spacer
                if ( block.__typename === `${blocksHandle}ContentBlocks_spacer_BlockType` ) {
                    return (
                        <div key={block.id} className={`block bg-${block.theme}`}></div>
                    )
                }

                // divider heading
                if ( block.__typename === `${blocksHandle}ContentBlocks_dividerHeading_BlockType` ) {
                    return (
                        <Fragment key={block.id}>
                            { block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div> }
                            <SectionDivider heading={block.heading} theme={`bg-${block.theme}`} />
                        </Fragment>
                    )
                }

                // partners
                if ( block.__typename === `${blocksHandle}ContentBlocks_partnerGroups_BlockType` ) {

                    return (
                        <section key={block.id} className="block bg-default">

                            {
                                block.partnerGroups.length &&
                                block.partnerGroups.map( partnerGroup => {

                                    const handleSize = ( partnerGroup ) => {
                                        switch ( partnerGroup.size ) {
                                            case 'lg':
                                                return 'fs-2 mb-md'
                                                break

                                            case 'md':
                                                return 'fs-4 fw-600 mb-sm'
                                        
                                            default:
                                                return 'fs-6 fw-600 mb-sm'
                                                break
                                        }
                                    }

                                    return (
                                        <div key={partnerGroup.id} className="container center pt-sm">
                                            <p className={`${handleSize(partnerGroup)} caps c-primary`}>{ partnerGroup.title }</p>
                                            <LogoGrid>
                                                {
                                                    partnerGroup.partners.length &&
                                                    partnerGroup.partners.map( partner => {
                                                        return (
                                                            <a key={partner.id} href={`/partners/${partner.slug}`} aria-label="Partner logo">
                                                                <div className={partnersCSS.logo}>
                                                                    <Image 
                                                                        src={partner.partnerLogo[0].url} 
                                                                        width={partner.partnerLogo[0].width}
                                                                        height={partner.partnerLogo[0].height}
                                                                        layout='responsive'
                                                                        alt=""
                                                                    />
                                                                </div>
                                        
                                                            </a>
                                                        )
                                                    })
                                                }
                                            </LogoGrid>
                                        </div>
                                    )
                                })
                            }

                        </section>

                    )
                }

                // cms speakers (profile groups)
                if ( block.__typename === `${blocksHandle}ContentBlocks_profileGroups_BlockType` ) {

                    if ( !block.speakerProfileGroups.length ) return
                    
                    return (
                        <Fragment key={block.id}>
                            <section className={`block ${ block.theme ? `bg-${block.theme}` : `bg-default`}`}>
                                {
                                    block.speakerProfileGroups.map ( group => (
                                        <div key={group.id} className='container'>

                                            <SpeakerGrid decal="gap-sm">
                                                {
                                                    group.profiles.map( profile => {
                                                        return (
                                                            <SpeakerCard
                                                                key={profile.id}
                                                                url={`/speakers/${profile.slug}`}
                                                                photoUrl={profile.avatar[0] ? profile.avatar[0].url : null }
                                                                fullName={profile.title}
                                                                jobTitle={profile.positions[0]?.jobTitle}
                                                                company={profile.positions[0]?.organisation}
                                                            />
                                                        )
                                                    })
                                                }
                                            </SpeakerGrid>
                                            
                                        </div>
                                    ))
                                }
                            </section>
                        </Fragment>
                    )
                }

                // testimonials
                if ( block.__typename === `${blocksHandle}ContentBlocks_testimonials_BlockType` ) {
                    
                    // console.log(block)

                    let theme
                    block.theme === 'default' ? theme = `bg-white` : theme = `bg-${block.theme}`

                    return (
                        <Fragment key={block.id}>
                            {block.anchorId && <div id={block.anchorId} className={`scrollShim`}></div>}
                            
                            <section className={`block ${theme}`}>
                                <div className={`container columns-${block.testimonials.length > 2 ? '2' : block.testimonials.length} gap-lg`}>
                                    {
                                        block.testimonials.map(quote => (
                                            <div key={quote.id}>
                                                <Quote 
                                                    avatar={quote.avatar}
                                                    quote={quote.quote} 
                                                    name={quote.fullName} 
                                                    title={`${quote.jobTitle ? quote.jobTitle : ''}${quote.jobTitle && quote.organisation ? ',' : ''} ${quote.organisation}`}
                                                    decal={`fs-3 caps`}
                                                />
                                            </div>
                                        ))
                                    }
                                </div>
                            </section>


                        </Fragment>
                    )
                }


                // CUSTOM: module inject placeholder
                if ( block.__typename === `${blocksHandle}ContentBlocks_customModule_BlockType` ) {
                    if (!block.content) return

                    return (
                        <Fragment key={block.id}>
                            {block.content}
                        </Fragment>
                    )
                }


            })}
        </Fragment>
    )


}

export default handleContentBlocks